import React, { useState, useContext, useCallback } from 'react';
import {
  X, Phone, MessageCircle, Mail, Globe, Baby, Clock, Edit3,
  Trash2, Plus, ChevronRight, User, Calendar, FileText, MessageSquare,
  ArrowRight, History, MapPin, CheckCircle2, XCircle, RotateCcw, AlertCircle, ExternalLink
} from 'lucide-react';
import NumberFormat from 'react-number-format';
import { LanguageContext, CenterContext, ClassesContext } from '../../../../Context';
import Staff from '../../components/Staff';
import InlineLocationSelect from '../../components/InlineLocationSelect';
import ClassSelect from './ClassSelect';



/**
 * LeadProfile - Modal dialog showing full lead details with tabs for
 * Info, Children, Contact Log, and History (status change timeline).
 * Includes inline auto-save test scheduling (book-test) and
 * test outcome (test) sections.
 *
 * @param {Object} props
 * @param {Object|null} props.lead - The selected lead, or null to hide
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onEdit - Callback to edit the lead
 * @param {Function} props.onDelete - Callback to delete the lead
 * @param {Function} props.onAddContact - Callback to add a contact log
 * @param {Function} props.onEnrol - Callback to enrol the lead as customer
 * @param {Function} props.onUpdateLead - Callback to update lead fields (auto-save)
 * @param {Object[]} props.stages - Stage configuration array
 * @returns {JSX.Element}
 * @author Forge, opencode glm-5.1
 * @version 5.0
 * @since 2026-04-16
 */

const METHOD_ICONS = {
  call: Phone,
  zalo: MessageCircle,
  visit: MessageSquare,
  video: Globe,
  meeting: User,
  other: FileText,
};

const STATUS_CONFIG = {
  scheduled: { icon: Calendar, label: 'Scheduled', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  completed: { icon: CheckCircle2, label: 'Completed', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
  'no-show': { icon: XCircle, label: 'No-show', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  rescheduled: { icon: RotateCcw, label: 'Rescheduled', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
};

const toZaloLink = (phone) => `https://zalo.me/${phone.replace(/^0/, '84')}`;

export default function LeadProfile({ lead, onClose, onEdit, onDelete, onAddContact, onEnrol, onUpdateLead, stages }) {
  const { l, language } = useContext(LanguageContext);
  
  const { getLocation } = useContext(CenterContext);
  const { classes } = useContext(ClassesContext);
  const [activeTab, setActiveTab] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const resolvePlacementName = useCallback((val) => {
    if (!val) return '';
    if (val.length > 10 || val.startsWith('loc_')) {
      const cls = classes?.find((c) => c._id === val);
      if (cls) return cls.name;
    }
    return val;
  }, [classes]);

  const t = useCallback((key, fallback) => {
    try { return l[key]?.[language] || fallback || key; } catch { return fallback || key; }
  }, [l, language]);

  const TABS = [
    t('pipeline.info', 'Info'),
    t('pipeline.children', 'Children'),
    t('pipeline.contactLog', 'Contact Log'),
    t('pipeline.history', 'History'),
  ];

  const stageLabels = {
    'lead': t('pipeline.lead', 'Lead'),
    'add-zalo': t('pipeline.addZalo', 'Add Zalo'),
    'book-test': t('pipeline.bookTest', 'Book Test'),
    'test': t('pipeline.test', 'Test'),
    'enrol': t('pipeline.enrol', 'Enrol'),
  };

  if (!lead) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const stage = stages.find(s => s.id === lead.stage) || stages[0];

  const formatDateTime = (iso) => {
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yy = String(d.getFullYear()).slice(-2);
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${dd}/${mm}/${yy} ${hh}:${mi}`;
    } catch {
      return iso || '';
    }
  };

  const locationName = (() => {
    if (!lead.testLocation) return '';
    if (lead.testLocation.startsWith('loc_') || lead.testLocation.length > 10) {
      const loc = getLocation?.(lead.testLocation);
      return loc?.name || lead.testLocation;
    }
    return lead.testLocation;
  })();

  const testDateInfo = (() => {
    if (!lead.testDate) return null;
    const d = new Date(lead.testDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.floor((d - today) / (1000 * 60 * 60 * 24));
    return { isOverdue: diff < 0, isToday: diff === 0, isTomorrow: diff === 1, formatted: lead.testDate };
  })();

  const handleFieldUpdate = (field, value) => {
    if (onUpdateLead) {
      onUpdateLead(lead.id || lead._id, { [field]: value });
    }
  };

  const statusCfg = STATUS_CONFIG[lead.testStatus];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100" style={{ backgroundColor: stage.bg }}>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: stage.color }}
                />
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: stage.headerBg, color: '#fff' }}
                >
                  {stage.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={16} className="text-slate-600" />
                <h2 className="text-xl font-bold text-slate-800">
                  {lead.phone ? (
                    <NumberFormat displayType="text" format="###-###-####" value={lead.phone} />
                  ) : (
                    <span className="text-slate-400 italic">{t('pipeline.noPhone', 'No phone')}</span>
                  )}
                </h2>
              </div>
              {lead.name && (
                <p className="text-sm text-slate-600 mt-0.5 ml-7">{lead.name}</p>
              )}
              <div className="flex items-center gap-3 mt-1 ml-7">
                <p className="text-xs text-slate-500">
                  {t('pipeline.addedDate', 'Added')} {formatDateTime(lead.createdAt)}
                </p>
                {lead.addedBy && (
                  <p className="text-xs text-slate-500">
                    {t('pipeline.by', 'by')} <span className="font-medium text-slate-700"><Staff id={lead.addedBy} /></span>
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-white/60 transition-colors duration-150 cursor-pointer"
            >
              <X size={18} />
            </button>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 mt-3">
            {lead.stage === 'add-zalo' && lead.zalo && (
              <a
                href={toZaloLink(lead.zalo)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold text-white transition-colors duration-150 cursor-pointer"
                style={{ backgroundColor: '#0077CC' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0066aa'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0077CC'; }}
              >
                <MessageCircle size={13} />
                {t('pipeline.openInZalo', 'Open in Zalo')}
                <ExternalLink size={11} />
              </a>
            )}
            <button
              onClick={() => onAddContact(lead)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold text-white transition-colors duration-150 cursor-pointer"
              style={{ backgroundColor: stage.headerBg }}
            >
              <Plus size={13} />
              {t('pipeline.logContact', 'Log Contact')}
            </button>
            <button
              onClick={() => onEdit(lead)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 transition-colors duration-150 cursor-pointer"
            >
              <Edit3 size={13} />
              {t('pipeline.editLead', 'Edit')}
            </button>
            {lead.stage === 'enrol' && (
              <button
                onClick={() => onEnrol(lead)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors duration-150 cursor-pointer"
              >
                <ChevronRight size={13} />
                {t('pipeline.createCustomer', 'Create Customer')}
              </button>
            )}
          </div>
        </div>

        {/* Inline test scheduling — Book Test stage */}
        {lead.stage === 'book-test' && (
          <div className="px-6 py-4 border-b border-amber-200 bg-amber-50/40">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={15} className="text-amber-600" />
              <h3 className="text-sm font-bold text-amber-800">
                {t('pipeline.testScheduling', 'Test Scheduling')}
              </h3>
              {testDateInfo?.isOverdue && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  <AlertCircle size={9} />
                  {t('pipeline.overdue', 'Overdue')}
                </span>
              )}
              {testDateInfo?.isToday && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  {t('pipeline.today', 'Today')}
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">
                  {t('pipeline.testDate', 'Test Date')}
                </label>
                <input
                  type="date"
                  value={lead.testDate || ''}
                  onChange={(e) => handleFieldUpdate('testDate', e.target.value)}
                  className="w-full px-3 py-2 rounded-[10px] border border-amber-200 text-sm text-slate-800 bg-white focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/20 transition-colors duration-150"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">
                  {t('pipeline.testTime', 'Test Time')}
                </label>
                <input
                  type="time"
                  value={lead.testTime || ''}
                  onChange={(e) => handleFieldUpdate('testTime', e.target.value)}
                  className="w-full px-3 py-2 rounded-[10px] border border-amber-200 text-sm text-slate-800 bg-white focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/20 transition-colors duration-150"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">
                  {t('pipeline.testLocation', 'Location')}
                </label>
                <InlineLocationSelect
                  value={lead.testLocation || ''}
                  setValue={(val) => handleFieldUpdate('testLocation', val)}
                  size="small"
                  placeholder={t('pipeline.selectLocation', 'Select location')}
                />
              </div>
            </div>
          </div>
        )}

        {/* Inline test outcome — Test stage */}
        {lead.stage === 'test' && (
          <div className="px-6 py-4 border-b border-orange-200 bg-orange-50/40">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={15} className="text-orange-600" />
              <h3 className="text-sm font-bold text-orange-800">
                {t('pipeline.testOutcome', 'Test Outcome')}
              </h3>
            </div>

            {/* Test date/time/location summary (read-only) */}
            {(lead.testDate || locationName) && (
              <div className="flex items-center gap-4 mb-3 text-xs text-slate-600">
                {lead.testDate && (
                  <div className="flex items-center gap-1">
                    <Calendar size={11} className="text-slate-400" />
                    <span>{lead.testDate}{lead.testTime ? ` ${lead.testTime}` : ''}</span>
                  </div>
                )}
                {locationName && (
                  <div className="flex items-center gap-1">
                    <MapPin size={11} className="text-slate-400" />
                    <span>{locationName}</span>
                  </div>
                )}
              </div>
            )}

            {/* Status buttons */}
            <div className="mb-3">
              <label className="block text-[10px] font-semibold text-orange-700 uppercase tracking-wider mb-1.5">
                {t('pipeline.testStatus', 'Status')}
              </label>
              <div className="flex gap-2">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  const isActive = lead.testStatus === key;
                  return (
                    <button
                      key={key}
                      onClick={() => handleFieldUpdate('testStatus', key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                        isActive
                          ? 'border-current shadow-sm'
                          : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                      style={isActive ? { color: cfg.color, backgroundColor: cfg.bg, borderColor: cfg.border } : {}}
                    >
                      <Icon size={12} />
                      {t(`pipeline.${key}`, cfg.label)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Class selection */}
            <ClassSelect
              value={lead.placementResult || ''}
              onChange={(val) => handleFieldUpdate('placementResult', val)}
              testLocation={lead.testLocation}
              label={t('pipeline.selectClass', 'Select Class')}
            />
          </div>
        )}

        {/* Enrol stage — placement result summary (read-only) */}
        {lead.stage === 'enrol' && lead.placementResult && (
          <div className="px-6 py-3 border-b border-emerald-200 bg-emerald-50/40">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                <CheckCircle2 size={14} />
                {resolvePlacementName(lead.placementResult)}
              </span>
              {lead.testStatus && STATUS_CONFIG[lead.testStatus] && (
                <span className="text-xs text-slate-500">
                  {t('pipeline.testStatus', 'Status')}: {t(`pipeline.${lead.testStatus}`, STATUS_CONFIG[lead.testStatus].label)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-100 px-6">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`px-4 py-3 text-xs font-semibold transition-colors duration-150 border-b-2 cursor-pointer ${
                activeTab === i
                  ? 'text-slate-800 border-slate-800'
                  : 'text-slate-400 border-transparent hover:text-slate-600'
              }`}
            >
              {tab}
              {i === 2 && lead.contacts?.length > 0 && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                  {lead.contacts.length}
                </span>
              )}
              {i === 3 && lead.statusHistory?.length > 0 && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                  {lead.statusHistory.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
        {activeTab === 0 && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              {lead.phone && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Phone size={14} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">{t('pipeline.phone', 'Phone')}</p>
                    <p className="text-sm text-slate-700 font-medium">
                      <NumberFormat displayType="text" format="###-###-####" value={lead.phone} />
                    </p>
                  </div>
                </div>
              )}
              {lead.zalo && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={14} className="text-cyan-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">{t('pipeline.zalo', 'Zalo')}</p>
                    <a
                      href={toZaloLink(lead.zalo)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-cyan-600 hover:text-cyan-700 transition-colors duration-150"
                    >
                      {lead.zalo}
                      <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              )}
              {lead.email && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                    <Mail size={14} className="text-violet-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">{t('pipeline.email', 'Email')}</p>
                    <p className="text-sm text-slate-700 font-medium">{lead.email}</p>
                  </div>
                </div>
              )}
              {lead.source && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Globe size={14} className="text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">{t('pipeline.source', 'Source')}</p>
                    <p className="text-sm text-slate-700 font-medium">{t(`pipeline.${lead.source}`, lead.source)}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                  <Clock size={14} className="text-slate-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">{t('pipeline.lastUpdated', 'Last Updated')}</p>
                  <p className="text-sm text-slate-700 font-medium">{formatDateTime(lead.updatedAt)}</p>
                </div>
              </div>
              {lead.addedBy && (
                 <div className="flex items-center gap-3">
<div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                     <User size={14} className="text-teal-800" />
                   </div>
                   <div>
                     <p className="text-xs text-slate-400">{t('pipeline.addedBy', 'Added By')}</p>
                     <p className="text-sm text-slate-700 font-medium"><Staff id={lead.addedBy} /></p>
                   </div>
                 </div>
               )}
                {lead.marketingPerson && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
                      <User size={14} className="text-rose-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">{t('pipeline.foundBy', 'Found By')}</p>
                      <p className="text-sm text-slate-700 font-medium"><Staff id={lead.marketingPerson} /></p>
                    </div>
                  </div>
                )}
                {lead.marketingContactCreatedAt && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
                      <Calendar size={14} className="text-rose-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">{t('pipeline.foundOn', 'Found On')}</p>
                      <p className="text-sm text-slate-700 font-medium">{formatDateTime(lead.marketingContactCreatedAt)}</p>
                    </div>
                  </div>
                )}
               {lead.location && (
                 <div className="flex items-center gap-3">
                   <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                     <MapPin size={14} className="text-emerald-500" />
                   </div>
                   <div>
                     <p className="text-xs text-slate-400">{t('pipeline.location', 'Location')}</p>
                     <p className="text-sm text-slate-700 font-medium">{lead.location}</p>
                   </div>
                 </div>
               )}
            </div>

            {/* Test scheduling info (read-only, for all stages that have test data) */}
            {(lead.stage !== 'book-test' && lead.stage !== 'test') && (lead.testDate || lead.testLocation || lead.placementResult) && (
              <div className="p-3 rounded-[10px] border border-amber-200 bg-amber-50/40">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">
                  {t('pipeline.testScheduling', 'Test Scheduling')}
                </p>
                <div className="flex items-center gap-5">
                  {lead.testDate && (
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-amber-600" />
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('pipeline.testDate', 'Test Date')}</p>
                        <p className="text-sm text-slate-700 font-medium">{lead.testDate}{lead.testTime ? ` ${lead.testTime}` : ''}</p>
                      </div>
                    </div>
                  )}
                  {locationName && (
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-amber-600" />
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('pipeline.testLocation', 'Location')}</p>
                        <p className="text-sm text-slate-700 font-medium">{locationName}</p>
                      </div>
                    </div>
                  )}
                  {lead.placementResult && (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-emerald-600" />
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('pipeline.placementResult', 'Placement')}</p>
                        <p className="text-sm text-slate-700 font-medium">{resolvePlacementName(lead.placementResult)}</p>
                      </div>
                    </div>
                  )}
                  {statusCfg && (
                    <div className="flex items-center gap-2">
                      <statusCfg.icon size={14} style={{ color: statusCfg.color }} />
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('pipeline.testStatus', 'Status')}</p>
                        <p className="text-sm font-medium" style={{ color: statusCfg.color }}>{t(`pipeline.${lead.testStatus}`, statusCfg.label)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {lead.notes && (
              <div className="p-3 rounded-[10px] bg-slate-50 border border-slate-100">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('pipeline.notes', 'Notes')}</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{lead.notes}</p>
              </div>
            )}

            {/* Delete */}
            <div className="pt-4 border-t border-slate-100">
              {confirmDelete ? (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-red-600 font-medium">{t('pipeline.deleteConfirm', 'Delete this lead?')}</p>
                  <button
                    onClick={() => { onDelete(lead.id || lead._id); setConfirmDelete(false); }}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors duration-150 cursor-pointer"
                  >
                    {t('pipeline.yesDelete', 'Yes, delete')}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
                  >
                    {t('pipeline.cancel', 'Cancel')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-500 transition-colors duration-150 cursor-pointer"
                >
                  <Trash2 size={13} />
                  {t('pipeline.deleteLead', 'Delete lead')}
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div className="p-6">
            {lead.children?.length > 0 ? (
              <div className="space-y-3">
                {lead.children.map((child, i) => (
                  <div
                    key={i}
                    className="p-4 rounded-[10px] bg-white border border-slate-200 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
                        <Baby size={14} className="text-teal-800" />
                      </div>
                      <h4 className="text-sm font-bold text-slate-800">
                        {child.name || `${t('pipeline.children', 'Child')} ${i + 1}`}
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 gap-2 ml-[42px]">
                      {child.age && (
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('pipeline.age', 'Age')}</p>
                          <p className="text-xs text-slate-700">{child.age}</p>
                        </div>
                      )}
                      {child.currentLevel && (
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('pipeline.level', 'Level')}</p>
                          <p className="text-xs text-slate-700">{child.currentLevel}</p>
                        </div>
                      )}
                    </div>
                    {child.notes && (
                      <p className="text-xs text-slate-500 mt-2 ml-[42px]">{child.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-3">
                  <Baby size={20} className="text-slate-300" />
                </div>
                <p className="text-sm text-slate-400">{t('pipeline.noChildrenYet', 'No children added yet')}</p>
                <button
                  onClick={() => onEdit(lead)}
className="mt-2 text-xs font-semibold text-teal-800 hover:text-teal-900 transition-colors duration-150 cursor-pointer"
                  >
                    {t('pipeline.addChildren', 'Add children')}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 2 && (
          <div className="p-6">
            {lead.contacts?.length > 0 ? (
              <div className="space-y-2">
                {[...lead.contacts].reverse().map((contact, i) => {
                  const MethodIcon = METHOD_ICONS[contact.method] || FileText;
                  return (
                    <div
                      key={contact.id || contact._id || i}
                      className="p-3 rounded-[10px] bg-white border border-slate-200 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
                    >
                      <div className="flex items-center gap-2.5 mb-1.5">
                        <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                          <MethodIcon size={13} className="text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-slate-700 capitalize">
                                {t(`pipeline.${contact.method === 'call' ? 'phoneCall' : contact.method}`, contact.method)}
                              </span>
                              {contact.contactedBy && (
                                <span className="text-[10px] text-slate-400">
                                  {t('pipeline.by', 'by')} <Staff id={contact.contactedBy} />
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-400">
                              {formatDateTime(contact.date)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {contact.outcome && (
                        <span className="inline-block ml-[38px] mb-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {contact.outcome}
                        </span>
                      )}
                      {contact.notes && (
                        <p className="text-xs text-slate-500 ml-[38px]">{contact.notes}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-3">
                  <Calendar size={20} className="text-slate-300" />
                </div>
                <p className="text-sm text-slate-400">{t('pipeline.noContactsYet', 'No contacts logged yet')}</p>
                <button
                  onClick={() => onAddContact(lead)}
                  className="mt-2 text-xs font-semibold text-teal-800 hover:text-teal-900 transition-colors duration-150 cursor-pointer"
                >
                  {t('pipeline.logFirstContact', 'Log first contact')}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 3 && (
          <div className="p-6">
            {lead.statusHistory?.length > 0 ? (
              <div className="relative">
                <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200" />

                <div className="space-y-4">
                  {[...lead.statusHistory].reverse().map((entry, i) => {
                    const fromStage = stages.find(s => s.id === entry.from);
                    const toStage = stages.find(s => s.id === entry.to);
                    const isInitial = entry.from === null;

                    return (
                      <div key={entry._id || i} className="relative flex items-start gap-4">
                        <div
                          className="w-[31px] h-[31px] rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 border-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                          style={{ backgroundColor: toStage?.color || '#64748b' }}
                        >
                          {isInitial ? (
                            <Plus size={12} className="text-white" />
                          ) : (
                            <ArrowRight size={12} className="text-white" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0 pt-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isInitial ? (
                              <span className="text-sm font-semibold text-slate-800">
                                {t('pipeline.createdAs', 'Created as')}{' '}
                                <span
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                                  style={{ backgroundColor: toStage?.headerBg || '#334155' }}
                                >
                                  {stageLabels[entry.to] || entry.to}
                                </span>
                              </span>
                            ) : (
                              <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                                <span
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                                  style={{ backgroundColor: fromStage?.headerBg || '#334155' }}
                                >
                                  {stageLabels[entry.from] || entry.from}
                                </span>
                                <ArrowRight size={12} className="text-slate-400" />
                                <span
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                                  style={{ backgroundColor: toStage?.headerBg || '#334155' }}
                                >
                                  {stageLabels[entry.to] || entry.to}
                                </span>
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-slate-400">
                              {formatDateTime(entry.date)}
                            </span>
                            {entry.changedBy && (
                              <span className="text-xs text-slate-400">
                                {t('pipeline.by', 'by')} <span className="font-medium text-slate-600"><Staff id={entry.changedBy} /></span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-3">
                  <History size={20} className="text-slate-300" />
                </div>
                <p className="text-sm text-slate-400">{t('pipeline.noStatusHistory', 'No status changes recorded')}</p>
                <p className="text-xs text-slate-300 mt-1">
                  {t('pipeline.historyTracked', 'History is tracked when leads move between stages')}
                </p>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}